import React, { useRef, useEffect, useState } from "react";
import logoNx from "../image/logo_nx.png";
import logoVN from "../image/logo_vn.png";

export default function PrintBillModal({ isOpen, onClose, billInfo }) {
  const printRef = useRef();
  const [storeConfig, setStoreConfig] = useState(null);
  const [selectedLogo, setSelectedLogo] = useState(logoNx);

  useEffect(() => {
    // Fetch store config from session storage
    const storedConfig = sessionStorage.getItem('storeConfig');
    if (storedConfig) {
      try {
        const config = JSON.parse(storedConfig);
        setStoreConfig(config);
        
        // Determine which logo to use based on storeId
        if (config.storeId && config.storeId.toLowerCase() === 'nx') {
          setSelectedLogo(logoNx);
        } else {
          setSelectedLogo(logoVN);
        }
      } catch (error) {
        console.error('Error parsing store config:', error);
      }
    }
  }, []);

  useEffect(() => {
    if (isOpen && storeConfig && billInfo) {
      // Small delay to ensure the component is fully rendered
      setTimeout(() => {
        handlePrint();
      }, 100);
    }
  }, [isOpen, storeConfig, billInfo]);

  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;

    const windowPrint = window.open('', '', 'width=800,height=600');
    
    windowPrint.document.write(`
      <html>
        <head>
          <title>Print Bill</title>
          <style>
            @page {
              margin: 0;
              size: auto;
            }
            body {
              font-family: Arial, sans-serif;
              padding: 10px;
              margin: 0;
            }
            * {
              color: #000 !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              font-family: Arial, sans-serif;
            }
            .printArea {
              font-size: 14px;
              line-height: 1.4;
            }
            .logo {
              text-align: center;
              margin-bottom: 8px;
            }
            .logo img {
              max-width: 80px;
              height: auto;
            }
            .composition-text {
              text-align: center;
              font-size: 10px;
              margin-bottom: 10px;
              font-style: italic;
            }
            .title {
              text-align: center;
              font-size: 11px;
              line-height: 1.3;
              margin-bottom: 10px;
            }
            .gst {
              text-align: center;
              font-size: 12px;
              margin-bottom: 10px;
            }
            .bill-of-supply {
              text-align: center;
              font-size: 14px;
              font-weight: bold;
              margin: 10px 0;
              text-decoration: underline;
            }
            .table {
              width: 100%;
              border-collapse: collapse;
              font-size: 13px;
              margin-top: 10px;
            }
            .table th,
            .table td {
              border-bottom: 1px solid #000;
              text-align: left;
              padding: 4px 2px;
            }
            .table th {
              font-weight: bold;
            }
            .size-cell {
              word-break: break-all;
              word-wrap: break-word;
              overflow-wrap: break-word;
              max-width: 35px;
            }
            .total {
              text-align: right;
              font-weight: bold;
              margin-top: 10px;
              font-size: 15px;
            }
            .footer {
              margin-top: 15px;
              font-size: 11px;
              line-height: 1.4;
            }
            .footer-title {
              font-weight: bold;
              text-decoration: underline;
              margin-bottom: 5px;
            }
            .bill-date-row {
              display: flex;
              justify-content: space-between;
              align-items: center;
            }
            hr {
              border: none;
              border-top: 1px solid #000 !important;
            }
            @media print {
              body { 
                padding: 5px;
              }
            }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    
    windowPrint.document.close();
    windowPrint.focus();
    
    setTimeout(() => {
      windowPrint.print();
      windowPrint.close();
      // Close the modal after printing
      onClose();
    }, 250);
  };

  // Helper function to render address lines
  const renderAddress = () => {
    if (!storeConfig) return null;
    
    const addressLines = [
      storeConfig.addressLine1,
      storeConfig.addressLine2,
      storeConfig.addressLine3,
      storeConfig.addressLine4
    ].filter(line => line && line.trim() !== "");

    return addressLines.map((line, index) => (
      <div key={index}>{line}</div>
    ));
  };

  // Check if customer info is present
  const hasCustomerInfo = () => {
    return (billInfo.customerName && billInfo.customerName.trim() !== "") || 
           (billInfo.contactNumber && billInfo.contactNumber.trim() !== "");
  };

  if (!isOpen || !billInfo) return null;

  return (
    <div style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}>
      <div 
        ref={printRef}
        style={{
          padding: '15px',
          background: '#fff',
          fontSize: '14px',
          lineHeight: '1.4',
          fontFamily: 'Arial, sans-serif',
          width: '380px'
        }}
      >
        {/* Logo */}
        <div style={{
          textAlign: 'center',
          marginBottom: '8px'
        }}>
          <img 
            src={selectedLogo} 
            alt="Logo" 
            style={{ 
              maxWidth: '80px', 
              height: 'auto',
              margin: '0 auto'
            }} 
          />
        </div>

        {/* Shop Address - Dynamic */}
        {storeConfig && (
          <div style={{
            textAlign: 'center',
            fontSize: '11px',
            lineHeight: '1.3',
            marginBottom: '10px'
          }}>
            {renderAddress()}
          </div>
        )}

        {/* GST Number - Dynamic */}
        {storeConfig && storeConfig.gst && (
          <div style={{
            textAlign: 'center',
            fontSize: '12px',
            marginBottom: '10px'
          }}>
            <strong>GST No:</strong> {storeConfig.gst}
          </div>
        )}

        <hr style={{ border: 'none', borderTop: '1px dashed #aaa', margin: '8px 0' }} />

        {/* Bill No and Date on same line */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <strong>Bill No:</strong> {billInfo.billNo}
          </div>
          <div>
            <strong>Date:</strong> {billInfo.date}
          </div>
        </div>

        {/* Show hr only if customer info is present */}
        {hasCustomerInfo() && (
          <hr style={{ border: 'none', borderTop: '1px dashed #aaa', margin: '8px 0' }} />
        )}
        
        {/* Customer Name - Show only if present */}
        {billInfo.customerName && billInfo.customerName.trim() !== "" && (
          <div>
            <strong>Customer Name:</strong> {billInfo.customerName}
          </div>
        )}
        
        {/* Contact Number - Show only if present */}
        {billInfo.contactNumber && billInfo.contactNumber.trim() !== "" && (
          <div>
            <strong>Contact:</strong> {billInfo.contactNumber}
          </div>
        )}
        
        <hr style={{ border: 'none', borderTop: '1px dashed #aaa', margin: '8px 0' }} />

        {/* Bill of Supply */}
        <div style={{
          textAlign: 'center',
          fontSize: '14px',
          fontWeight: 'bold',
          margin: '10px 0',
          textDecoration: 'underline'
        }}>
          Bill of Supply
        </div>

        {/* Composition Taxable Person */}
        <div style={{
          textAlign: 'center',
          fontSize: '11px',
          marginBottom: '10px',

        }}>
          Composition Taxable Person Not Eligible To Collect Tax On Supplies
        </div>

        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: '13px',
          marginTop: '10px',
          tableLayout: 'fixed'
        }}>
          <colgroup>
            <col style={{ width: '25px' }} />
            <col style={{ width: '160px' }} />
            <col style={{ width: '35px' }} />
            <col style={{ width: '45px' }} />
            <col style={{ width: '30px' }} />
            <col style={{ width: '55px' }} />
          </colgroup>
          <thead>
            <tr>
              <th style={{ borderBottom: '1px dashed #aaa', textAlign: 'left', padding: '4px 2px' }}>S.No</th>
              <th style={{ borderBottom: '1px dashed #aaa', textAlign: 'left', padding: '4px 30px' }}>Description</th>
              <th style={{ borderBottom: '1px dashed #aaa', textAlign: 'left', padding: '4px 2px' }}>Size</th>
              <th style={{ borderBottom: '1px dashed #aaa', textAlign: 'left', padding: '4px 2px' }}>Price</th>
              <th style={{ borderBottom: '1px dashed #aaa', textAlign: 'left', padding: '4px 2px' }}>Qty</th>
              <th style={{ borderBottom: '1px dashed #aaa', textAlign: 'left', padding: '4px 2px' }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {billInfo.billingItemsList.map((item) => (
              <tr key={item.sno}>
                <td style={{ borderBottom: '1px dashed #aaa', padding: '4px 2px' }}>{item.sno}</td>
                <td style={{ borderBottom: '1px dashed #aaa', padding: '4px 20px' }}>{item.description}</td>
                <td style={{ 
                  borderBottom: '1px dashed #aaa', 
                  padding: '4px 2px',
                  wordBreak: 'break-all',
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                  maxWidth: '35px'
                }}>{item.size || '-'}</td>
                <td style={{ borderBottom: '1px dashed #aaa', padding: '4px 2px' }}>{item.price}</td>
                <td style={{ borderBottom: '1px dashed #aaa', padding: '4px 2px' }}>{item.qty}</td>
                <td style={{ borderBottom: '1px dashed #aaa', padding: '4px 2px' }}>{item.amount}</td>
              </tr>
            ))}
            
            {/* Total Quantities Row */}
            {billInfo.totalQuantities && (
              <tr>
                <td style={{ padding: '6px 2px' }}></td>
                <td style={{ padding: '6px 2px' }}></td>
                <td colSpan="2" style={{ padding: '6px 2px', textAlign: 'right', fontWeight: 'bold', whiteSpace: 'nowrap' }}>Total Qty: {billInfo.totalQuantities}</td>
                <td style={{ padding: '6px 2px' }}></td>
                <td style={{ padding: '6px 2px' }}></td>
              </tr>
            )}
          </tbody>
        </table>

        <div style={{
          textAlign: 'right',
          fontWeight: 'bold',
          marginTop: '10px',
          fontSize: '15px'
        }}>
          Total Amount: ₹{billInfo.totalAmount}
        </div>

        {/* Discount - Show only if present */}
        {billInfo.discount && (
          <div style={{
            textAlign: 'right',
            marginTop: '5px',
            fontSize: '14px'
          }}>
            <strong>Discount:</strong> ₹{billInfo.discount}
          </div>
        )}

        {billInfo.finalAmountAfterDiscount && (
          <div style={{
            textAlign: 'right',
            fontWeight: 'bold',
            marginTop: '5px',
            fontSize: '15px'
          }}>
            Grand Total: ₹{billInfo.finalAmountAfterDiscount}
          </div>
        )}

        {/* Total Items */}
        {billInfo.totalItems && (
          <div style={{
            textAlign: 'right',
            marginTop: '8px',
            fontSize: '13px'
          }}>
            <strong>Total Items:</strong> {billInfo.totalItems}
          </div>
        )}

        <hr style={{ border: 'none', borderTop: '1px dashed #aaa', margin: '12px 0' }} />

        {/* Footer - Terms and Conditions */}
        <div style={{
          marginTop: '15px',
          fontSize: '11px',
          lineHeight: '1.4'
        }}>
          <div style={{
            fontWeight: 'bold',
            textDecoration: 'underline',
            marginBottom: '5px'
          }}>
            Terms and Conditions
          </div>
          <div>1. Good once sold will not be returned</div>
          <div>2. No claim No gauranty</div>
          <div>3. Exchange within 7 days with bill</div>
          <div>4. Goods with stains or if found used will not be exchanged</div>
        </div>
      </div>
    </div>
  );
}